import { Alert, AppLoader, Header, Modal } from '@bbl-digital/snorre'
import { FC, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'shared/store/store'
import SkeletonList from 'shared/components/skeletons/SkeletonList'
import { SendMessageStatusEnum } from 'shared/models/Message'
import {
  SEND_EMAIL_REFETCH_SENDING_STATUS_REQUESTED,
  SEND_EMAIL_RESET,
} from 'shared/store/sendMessage/email/actionTypes'
import {
  getEmailReciept,
  getSendEmailId,
} from 'shared/store/sendMessage/email/selectors'
import { SEND_SMS_REFETCH_SENDING_STATUS_REQUESTED } from 'shared/store/sendMessage/sms/actionTypes'
import {
  getSendSmsId,
  getSmsReciept,
} from 'shared/store/sendMessage/sms/selectors'
import CollapsedMessage from '../CollapsedMessage'
import RecieptInformation from './RecieptInformation'
import { Column, HeaderWrapper, ModalWrapper } from './styles'

interface IProps {
  isSms?: boolean
  onClose: () => void
}

const MessageReciept: FC<IProps> = ({ isSms, onClose }) => {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isSlowSending, setIsSlowSending] = useState(false)
  const dispatch = useAppDispatch()
  const {
    data: emailData,
    loading: emailLoading,
    createContact: emailCreateContact,
    error: emailError,
    processing: emailProcessing,
  } = useAppSelector(getEmailReciept)
  const {
    data: smsData,
    loading: smsLoading,
    createContact: smsCreateContact,
    error: smsError,
    processing: smsProcessing,
  } = useAppSelector(getSmsReciept)
  const emailId = useAppSelector(getSendEmailId)
  const smsId = useAppSelector(getSendSmsId)

  const data = isSms ? smsData : emailData
  const loading = isSms ? smsLoading : emailLoading
  const createContact = isSms ? smsCreateContact : emailCreateContact
  const error = isSms ? smsError : emailError
  const processing = isSms ? smsProcessing : emailProcessing
  const isSending = data?.sendingStatus === SendMessageStatusEnum.Sending
  const sendingError = data?.sendingStatus === SendMessageStatusEnum.Error

  useEffect(() => {
    const interval =
      isSending &&
      setInterval(() => {
        // Backup in case user don't recieve signalR message for some reason, will fetch
        // sending status from api every 15 seconds until message is sent or modal is closed
        dispatch({
          type: isSms
            ? SEND_SMS_REFETCH_SENDING_STATUS_REQUESTED
            : SEND_EMAIL_REFETCH_SENDING_STATUS_REQUESTED,
          payload: isSms ? smsId : emailId,
        })
        // Wait 1 second before showing slow loading alert to allow api to finish first
        setTimeout(() => setIsSlowSending(true), 1000)
      }, 15000)
    return () => {
      if (interval) clearInterval(interval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSending])

  const handleClose = () => {
    // Reset body overflow to fix scroll lock left behind by body-scroll-lock
    // race condition during SendEmail → MessageReciept modal transition
    document.body.style.overflow = ''
    dispatch({ type: SEND_EMAIL_RESET })
    onClose()
  }

  return (
    <>
      {isCollapsed && (
        <CollapsedMessage
          title={(isSms ? 'SMS' : 'E-post') + ' kvittering'}
          onClose={handleClose}
          onOpen={() => setIsCollapsed(false)}
          type={isSms ? 'sms' : 'email'}
        />
      )}

      <ModalWrapper collapsed={isCollapsed}>
        <Modal
          open
          disableScroll={false}
          hideCloseButton={!!createContact}
          onCollapse={() => setIsCollapsed(true)}
          header={
            <Header level={4}>
              <HeaderWrapper>
                {createContact
                  ? 'Legg til ny kontakt'
                  : `${isSms ? 'SMS-en' : 'E-posten'} ${
                      isSending
                        ? 'sendes'
                        : sendingError
                          ? 'ble ikke sendt'
                          : 'ble sendt'
                    }`}
                {isSending && <AppLoader />}
              </HeaderWrapper>
            </Header>
          }
          onClose={handleClose}
        >
          <Column gap="1em">
            {isSlowSending && isSending && (
              <Alert>
                Det tok litt lenger tid å sende denne{' '}
                {isSms ? 'SMS-en' : 'e-posten'} enn antatt. Når{' '}
                {isSms ? 'SMS-en' : 'e-posten'} er sendt vil den dukke opp i
                mappen "Sendte". Denne dialogen kan lukkes.
              </Alert>
            )}

            {data && !loading && (
              <RecieptInformation
                reciept={data}
                createContact={createContact}
                error={error}
                isSms={isSms}
                processing={processing}
                isSending={isSending}
                onClose={handleClose}
              />
            )}

            {loading && <SkeletonList differ length={10} />}

            {error && <Alert danger>{error}</Alert>}
          </Column>
        </Modal>
      </ModalWrapper>
    </>
  )
}

export default MessageReciept
